import { useFeatureQuery } from "@di-devs/better-feature/client";
import type { AtomListener } from "@di-devs/better-feature/types";
import { atom, type WritableAtom } from "nanostores";
import type { EndpointConfig, QuerySet, QuerySetMap } from "./types";

export function createQuerySet<T>(
	name: string,
	config: EndpointConfig<T>,
	$fetch: Parameters<typeof useFeatureQuery>[2],
): QuerySet<T> {
	const signalName = `$${name}Signal`;
	const signal = atom<boolean>(false);

	const query = useFeatureQuery<T>(signal, config.path, $fetch, {
		method: config.method,
	});

	const listener = config.listener || {
		matcher: createPathMatcher(config.path),
		signal: signalName,
	};

	return { signal, query, listener };
}

export function createQuerySetMap<
	T extends Record<string, EndpointConfig<any>>,
>(configs: T, $fetch: Parameters<typeof useFeatureQuery>[2]): QuerySetMap<T> {
	const result = {} as QuerySetMap<T>;

	for (const [name, config] of Object.entries(configs)) {
		result[name as keyof T] = createQuerySet(name, config, $fetch) as any;
	}

	return result;
}

function createPathMatcher(path: string): (testPath: string) => boolean {
	return (testPath: string) => {
		if (path === testPath) return true;

		if (path.includes("*")) {
			const pattern = path.replace(/\*/g, ".*");
			return new RegExp(`^${pattern}$`).test(testPath);
		}

		if (path.endsWith("/*")) {
			const basePath = path.slice(0, -2);
			return testPath.startsWith(basePath);
		}

		return false;
	};
}

export function extractSignals<T extends QuerySetMap<any>>(
	querySets: T,
): Record<keyof T, WritableAtom<boolean>> {
	const signals = {} as Record<keyof T, WritableAtom<boolean>>;

	for (const [name, querySet] of Object.entries(querySets)) {
		signals[name as keyof T] = querySet.signal;
	}

	return signals;
}

export function extractQueries<T extends QuerySetMap<any>>(
	querySets: T,
): Record<keyof T, ReturnType<typeof useFeatureQuery>> {
	const queries = {} as Record<keyof T, ReturnType<typeof useFeatureQuery>>;

	for (const [name, querySet] of Object.entries(querySets)) {
		queries[name as keyof T] = querySet.query;
	}

	return queries;
}

export function extractListeners<T extends QuerySetMap<any>>(
	querySets: T,
): AtomListener[] {
	return Object.values(querySets)
		.map((querySet) => querySet.listener)
		.filter(Boolean);
}
